import App from "./components/app";
import AuthService from "./services/auth-service";

import { MsalConfigAD, MsalConfigADB2C } from "./config/auth";
import { MSAL_REDIRECT_PAGE_URL } from "./constants";
import { captureOwnerStack } from "react";
import { createRoot, type ErrorInfo } from "react-dom/client";
import { asyncWithLDProvider } from "launchdarkly-react-client-sdk";
import * as sentry from "@sentry/react";

import "./index.scss";
import "./tailwind.css";

const isAD = globalThis.localStorage.getItem("msalDirectory") === "ad"; // Or 'adb2c'
const globalMsalConfig = isAD ? MsalConfigAD : MsalConfigADB2C;

const logRenderError = (
    label: string,
    error: unknown,
    errorInfo: ErrorInfo,
) => {
    const url =
        globalThis.location.pathname +
        globalThis.location.search +
        globalThis.location.hash;
    const ownerStack = captureOwnerStack();

    console.group(`[${label}] ${url}`);
    if (error instanceof Error) {
        console.error(`${error.name}: ${error.message}`);
        if (error.stack) console.error(error.stack);
    } else {
        console.error(error);
    }
    if (errorInfo.componentStack) {
        console.error("Component stack:" + errorInfo.componentStack);
    }
    if (ownerStack) {
        console.error("Owner stack:" + ownerStack);
    }
    console.groupEnd();
};

const reportToSentry = (error: unknown, errorInfo: ErrorInfo) => {
    if (error instanceof Error) {
        sentry.captureException(error, {
            contexts: {
                react: { componentStack: errorInfo.componentStack ?? "" },
            },
        });
    } else if (typeof error === "string") {
        sentry.captureMessage(error, "fatal");
    } else {
        sentry.captureMessage(
            `Unknown render error at: ${globalThis.location.pathname}${globalThis.location.search}${globalThis.location.hash}`,
        );
    }
};

// For local feature flag testing use local launchdarkly environment clientSideID. I.E: SET VITE_LAUNCHDARKLY_API_KEY="your clientSideID"
const clientSideID = import.meta.env.VITE_LAUNCHDARKLY_API_KEY;

// IL-4027: From MSAL v5, silent token renewal loads the redirect URI inside a hidden iframe
// (and interactive logins return to it in the top window). This page must run ONLY the MSAL
// redirect bridge — booting the SPA here re-invokes MSAL/SignalR inside the iframe and throws
// block_iframe_reload, breaking silent SSO. See https://aka.ms/msal.js.errors#block_iframe_reload
if (globalThis.location.pathname === MSAL_REDIRECT_PAGE_URL) {
    const { broadcastResponseToMainFrame } =
        await import("@azure/msal-browser/redirect-bridge");
    await broadcastResponseToMainFrame().catch((error) => console.error(error));
} else {
    const container = document.getElementById("root");
    const root = createRoot(container!, {
        onCaughtError: (error, errorInfo) => {
            logRenderError("ErrorBoundary caught", error, errorInfo);
            reportToSentry(error, errorInfo);
        },
        onUncaughtError: (error, errorInfo) => {
            logRenderError("Uncaught render error", error, errorInfo);
            reportToSentry(error, errorInfo);
        },
        onRecoverableError: (error, errorInfo) => {
            console.warn(
                "[Recoverable render error]",
                error instanceof Error ? error.message : error,
                errorInfo.componentStack ?? "",
            );
        },
    });

    /**
     * MSAL should be instantiated outside of the component tree to prevent it from being re-instantiated on re-renders.
     * For more, visit: https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-react/docs/getting-started.md
     */
    const publicClientApp =
        await AuthService.createPublicClientApplication(globalMsalConfig);
    if (clientSideID) {
        const LDProvider = await asyncWithLDProvider({
            clientSideID,
            reactOptions: { useCamelCaseFlagKeys: false },
        });

        root.render(
            <LDProvider>
                <App instance={publicClientApp} />
            </LDProvider>,
        );
    } else {
        // Directly render App without LaunchDarkly provider if VITE_LAUNCHDARKLY_API_KEY is undefined
        root.render(<App instance={publicClientApp} />);
    }
}
